<script setup lang="ts">
import { useField } from 'vee-validate'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ChevronDown from '/svg/chevron-down.svg'

interface CountryOption {
  label: string
  value: string
  flagUrl?: string
  iso?: string
  callingCode?: number
}

interface Props {
  name: string
  countryFieldName?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  modelValue?: string | number
  error?: string
  countries?: CountryOption[]
  isoToFaName: Record<string, string>
  appearance?: string
  defaultCountryId?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  name: '',
  placeholder: '',
  modelValue: '',
  countryFieldName: 'country',
  error: '',
  countries: () => [],
  appearance: '',
})

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const wrapper = ref<HTMLElement | null>(null)
const isFocused = ref(false)
const isOpen = ref(false)
const searchQuery = ref('')

const { value, errorMessage, handleBlur } = useField<string | number>(props.name, undefined, {
  initialValue: props.modelValue,
})

const { value: countryFieldValue, errorMessage: countryError } = useField<string | number>(
  props.countryFieldName,
  undefined,
  { initialValue: '' }
)

const hasError = computed(() => !!errorMessage.value || !!countryError.value)

const selectedCountry = ref<CountryOption | undefined>(undefined)

const getFaName = (country: CountryOption) =>
  (country.iso && props.isoToFaName[country.iso]) || country.label || 'نامشخص'

const filteredCountries = computed(() => {
  const list = props.countries || []
  if (!searchQuery.value) return list
  const q = searchQuery.value.toLowerCase()
  return list.filter(
    (c: CountryOption) =>
      (c.label && c.label.toLowerCase().includes(q)) ||
      (c.iso && c.iso.toLowerCase().includes(q)) ||
      (getFaName(c) && getFaName(c).includes(searchQuery.value))
  )
})

function updateCountry(val: string | number) {
  countryFieldValue.value = val
  selectedCountry.value = (props.countries || []).find((c: CountryOption) => c.value === val)
  isOpen.value = false
  searchQuery.value = ''
}

function toggleOpen() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    setTimeout(() => {
      const el = document.getElementById('country-search')
      el?.focus()
    }, 100)
  } else {
    searchQuery.value = ''
  }
}

function handleClickOutside(e: MouseEvent) {
  if (wrapper.value && !wrapper.value.contains(e.target as Node)) {
    isOpen.value = false
  }
}

watch(value, (v) => {
  emit('update:modelValue', v as string | number)
})

watch(
  () => props.countries,
  () => {
    selectedCountry.value = (props.countries || []).find((c: CountryOption) => c.value === countryFieldValue.value)
  },
  { immediate: true }
)

watch(
  [() => props.countries, () => props.defaultCountryId],
  ([countries, defaultId]) => {
    if (!defaultId || countryFieldValue.value) return
    if (countries.some((c) => c.value === defaultId)) {
      updateCountry(defaultId)
    }
  },
  { immediate: true }
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))
</script>

<template>
    <div class="relative flex flex-col gap-1 w-full" ref="wrapper">
        <label v-if="label" class="w-fit bg-white px-1 transition-all duration-400 z-30  pointer-events-none" :class="[(isFocused || value) ? 'absolute ms-4 -mt-2 text-[10px] font-bold' : 'absolute mt-3.5 ms-2 text-[13px] font-medium',
        appearance === 'admin' ? 'text-[#6C6C6C]' : 'text-[#1b325a]'
        ]">
            {{ label }}
        </label>

        <div class="relative w-full flex items-center rounded-xl border h-12 border-[#E0E0E0] bg-white   transition "
            :class="[hasError ? 'border-[#ed145b]' : '',
            appearance === 'shop' ? 'focus-within:border-[#2626bf] focus-within:outline-[var(--ring)] focus-within:outline-offset-2 focus-within:outline-2  focus-within:rounded-[6px]' : 'focus-within:border-black'
            ]">
            <input dir="ltr" :name="name" v-model="value" :disabled="disabled"
                class="w-[80%] px-2 me-1 font-fa text-sm font-medium text-[#1A1A1A]  outline-none bg-transparent focus-visible:outline-none! focus:ring-0 focus:outline-none focus:border-[#E0E0E0]"
                @focus="isFocused = true" @blur="() => { isFocused = false; handleBlur(); }" />
            <div class="h-11 border-start border-l border-[#E0E0E0] mx-3"></div>

            <div class="relative h-full pe-5 flex items-center">
                <div class="cursor-pointer flex items-center gap-0 select-none" @click.stop="toggleOpen">
                    <div v-if="selectedCountry?.callingCode"
                        class="font-fa text-sm font-medium text-nowrap text-gray-700 me-2">
                        {{ selectedCountry?.callingCode }}+
                    </div>
                    <img v-if="selectedCountry?.flagUrl" :src="selectedCountry.flagUrl"
                        class="w-6 h-4 rounded object-cover me-1" />
                    <span v-else class="text-[#1b325a] text-sm">🌐</span>
                    <img :src="ChevronDown" class="w-6 h-6 transition-transform duration-200 me-1"
                        :class="{ 'rotate-180': isOpen }" />
                </div>

                <div v-if="isOpen"
                    class="absolute top-full end-0 mt-2 bg-white border border-[#E0E0E0] rounded-xl shadow-lg z-[999] w-56 max-h-60 overflow-y-auto custom-dropdown">
                    <div class="sticky top-0 bg-white p-2 border-b border-[#E0E0E0]">
                        <input id="country-search" v-model="searchQuery" placeholder="جستجوی کشور..."
                            class="w-full px-2 py-1 text-sm border border-[#E0E0E0] rounded-lg outline-none focus-visible:outline-none! focus:ring-0 focus:outline-none " />
                    </div>

                    <div v-if="filteredCountries.length > 0">
                        <div v-for="c in filteredCountries" :key="c.value" @click="updateCountry(c.value)"
                            class="px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-[#f8f9fa] transition-all"
                            :class="c.value === countryFieldValue ? 'bg-[#DAE9F8] font-bold text-[#1b325a]' : 'text-[#1b325a]'">
                            <img v-if="c.flagUrl" :src="c.flagUrl" class="w-6 h-4 rounded object-cover" />
                            <span>{{ getFaName(c) }}</span>
                        </div>
                    </div>
                    <div v-else class="px-3 py-2 text-center text-gray-500 text-sm">کشوری یافت نشد</div>
                </div>
            </div>

        </div>

        <ErrorMessage :name="name" />
        <ErrorMessage :name="countryFieldName" />
    </div>
</template>


<style scoped>
.custom-dropdown::-webkit-scrollbar {
    width: 6px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>
